import ChatField from '@/components/feature/FAQ/chat-field';
import DropdownFilter from '@/components/feature/FAQ/dropdown';
import { useOrientation } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import { useConfigStore } from '@/stores';
import { buildCustomMenuDropdownSections } from '@/utils/custom-menu/dropdown';
import { getHSize, getSize } from '@/utils/size';
import { useMemo, useState } from 'react';
import styles from './FAQPage.module.scss';
import { useParams } from 'react-router-dom';

export default function FAQPage() {
    const { config } = useConfigStore();
    const { screenMode, language } = config.ui;
    const { server } = config;
    const { buildingMenus } = server || {};
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const currentMenu = useMemo(
        () => buildingMenus?.[language]?.find((item) => item.id === gnbMenuId),
        [buildingMenus, language, gnbMenuId]
    );

    const faqTitle = currentMenu?.name || 'This is title';
    const faqDescription = currentMenu?.description || '';

    const dropdownData = useMemo(
        () => buildCustomMenuDropdownSections(currentMenu?.customMenuCategory, currentMenu?.customContent),
        [currentMenu?.customMenuCategory, currentMenu?.customContent]
    );

    const { isHorizontal } = useOrientation();
    const { baseWidth, baseHeight } = useBaseSize();

    const [selectedFAQ, setSelectedFAQ] = useState<{ question: string; answer: string } | null>(null);

    const handleQuestionSelect = (question: string, answer: string) => {
        setSelectedFAQ({ question, answer });
    };

    // Calculate top margin based on screenMode
    const topMargin = screenMode === 'fullscreen' ? '0' : isHorizontal ? `${getHSize(171, baseHeight)}` : `${getHSize(868, baseHeight)}`;

    return (
        <div className={styles.faqContainer}>
            <div
                className="flex h-full w-full flex-col bg-[var(--color-bg-gray-f9)]"
                style={{
                    marginTop: topMargin,
                    ...(screenMode !== 'fullscreen' && {
                        height: isHorizontal ? `${getHSize(829, baseHeight)}` : `${getHSize(872, baseHeight)}`,
                        maxWidth: `${getSize(1920, baseWidth)}`,
                        overflow: 'hidden',
                    }),
                }}
            >
                {/* Header */}
                <header
                    data-region-nav
                    tabIndex={0}
                    data-interactive
                    className={`widescreen:pt-[calc(56*var(--vh-unit))] widescreen:fullscreen:pb-[calc(14*var(--vh-unit))] fullscreen:pb-[calc(64*var(--vh-unit))] widescreen:pb-[calc(14*var(--vh-unit))] widescreen:pl-[calc(67*var(--vw-unit))] widescreen:pr-[calc(68*var(--vw-unit))] widescreen:gap-[calc(16*var(--vh-unit))] widescreen:border-none focus-border flex flex-col gap-[calc(10*var(--vh-unit))] border-b-[calc(1*var(--vh-unit))] border-[var(--color-secondary-black-light)] px-[calc(56*var(--vw-unit))] pt-[calc(86*var(--vh-unit))] pb-[calc(4*var(--vh-unit))] text-center`}
                    aria-label={faqTitle + ' ' + faqDescription}
                >
                    <h1 className="widescreen:text-[calc(56*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(60*var(--vh-unit))] text-center text-2xl text-[calc(60*var(--vh-unit))] leading-[calc(90*var(--vh-unit))] font-bold text-[color:var(--color-text-main01)]">
                        {faqTitle}
                    </h1>
                    <p className="widescreen:text-[calc(26*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(30*var(--vh-unit))] text-center text-[calc(28*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium text-[color:var(--color-text-main01)]">
                        {faqDescription}
                    </p>
                </header>

                <main
                    className={`fullscreen:widescreen:px-[calc(121*var(--vw-unit))] widescreen:px-[calc(121*var(--vw-unit))] fullscreen:widescreen:pt-[calc(30*var(--vh-unit))] widescreen:pt-[calc(30*var(--vh-unit))] fullscreen:widescreen:pb-[calc(31*var(--vh-unit))] widescreen:pb-[calc(31*var(--vh-unit))] fullscreen:pb-[calc(79*var(--vh-unit))] fullscreen:pt-[calc(38*var(--vh-unit))] fullscreen:px-[calc(104*var(--vw-unit))] h-full px-[calc(104*var(--vw-unit))] py-[calc(23*var(--vh-unit))]`}
                >
                    {/* Left section */}
                    <div
                        className={`mx-auto flex w-fit justify-center rounded-[16px] border-[calc(1*var(--vh-unit))] border-[var(--color-border-transparent)]`}
                    >
                        <section
                            className={`widescreen:w-[calc(608*var(--vw-unit))] widescreen:h-[calc(588*var(--vh-unit))] widescreen:fullscreen:h-[calc(760*var(--vh-unit))] fullscreen:h-[calc(1260*var(--vh-unit))] bg-bg-white fullscreen:py-[calc(72*var(--vh-unit))] h-[calc(604*var(--vh-unit))] w-[calc(400*var(--vw-unit))] rounded-l-[16px] py-[calc(30*var(--vh-unit))] pr-[calc(24*var(--vw-unit))] pl-[calc(40*var(--vw-unit))]`}
                        >
                            <DropdownFilter data={dropdownData} onQuestionSelect={handleQuestionSelect} />
                        </section>

                        {/* Right section */}
                        <section
                            className={`bg-bg-f5f2ee-to-black widescreen:w-[calc(812*var(--vw-unit))] widescreen:px-[calc(60*var(--vw-unit))] widescreen:h-[calc(588*var(--vh-unit))] fullscreen:widescreen:h-[calc(760*var(--vh-unit))] fullscreen:h-[calc(1260*var(--vh-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 h-[calc(604*var(--vh-unit))] w-[calc(472*var(--vw-unit))] overflow-y-auto rounded-r-[16px] p-[calc(30*var(--vh-unit))]`}
                        >
                            <ChatField selectedQuestion={selectedFAQ?.question} selectedAnswer={selectedFAQ?.answer} />
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
}
